'use client';

import React from 'react';
import classnames from 'classnames';
import Box from '../../../atoms/Box/Box';
import Row from '../../../atoms/Grid/Row/Row';
import IconButton from '../../../atoms/IconButton/IconButton';
import Typography from '../../../atoms/Typography/Typography';
// import Switch from '../../../atoms/Switch/Switch';
import { SidebarIcon } from '../Icons';

import data from '../sidebar.json';

import styles from './Slider.module.scss';

interface IconConfig {
  category: string;
  name: string;
}

interface Workspace {
  id: string;
  path: string;
  label: string;
  icon?: IconConfig;
  childrens: Array<{
    id: string;
    label: string;
    path: string;
    icon?: IconConfig;
  }>;
}

interface SliderProps {
  isOpen: boolean;
  isClosing: boolean;
  isCollapsed: boolean;
  theme: 'light' | 'dark';
  onClose: () => void;
  // Workspace props
  variant?: 'workspace' | 'settings';
  selectedWorkspace?: string;
  onWorkspaceSelect?: (workspaceId: string) => void;
  workspaces?: Workspace[];
  // Settings props
  onToggleTheme?: () => void;
  languageSelector?: React.ReactNode;
  languageToggle?: React.ReactNode;
  rolesDropdown?: React.ReactNode;
  /** i18n labels */
  labels?: {
    workspace?: string;
    settings?: string;
    appearance?: string;
    darkMode?: string;
    lightMode?: string;
  };
}

const Slider: React.FC<SliderProps> = ({
  isOpen,
  isClosing,
  isCollapsed,
  theme,
  onClose,
  variant = 'workspace',
  selectedWorkspace,
  onWorkspaceSelect,
  workspaces,
  onToggleTheme: _onToggleTheme,
  languageSelector,
  languageToggle,
  rolesDropdown: _rolesDropdown,
  labels,
}) => {
  if (!isOpen) return null;

  const workspaceList = workspaces || data.data;
  const isSettings = variant === 'settings';
  const title = isSettings ? (labels?.settings ?? 'Settings') : (labels?.workspace ?? 'Workspace');

  return (
    <>
      {/* Overlay */}
      <div
        className={classnames(styles.overlay, {
          [styles.overlayClosing]: isClosing,
        })}
        onClick={onClose}
      />

      {/* Slider */}
      <div
        className={classnames(styles.workspaceSlider, {
          [styles.sliderCollapsed]: isCollapsed,
          [styles.sliderExpanded]: !isCollapsed,
          [styles.sliderClosing]: isClosing,
        })}
        data-theme={theme}
        data-slider="true"
      >
        <Box className={styles.sliderHeader} pt={24} pr={24} pb={24} pl={24}>
          <Row gutter={[0, 0]} justify="space-between" align="middle" wrap={false}>
            <Typography variant="subtitle" size="medium" color="white" theme={theme}>
              {title}
            </Typography>
            <IconButton
              icon={{ category: 'global', name: 'close' }}
              variant="button"
              buttonType="tertiary"
              handleClick={onClose}
              gaComponent={`${variant}-slider`}
              dataTestId={`${variant}-slider-close`}
            />
          </Row>
        </Box>

        <Box className={styles.sliderContent} pl={24} pr={24}>
          {/* Workspace Content */}
          {!isSettings &&
            workspaceList.map((workspace) => (
              <button
                key={workspace.id}
                className={classnames(styles.workspaceItem, {
                  [styles.selected]: selectedWorkspace === workspace.id,
                })}
                onClick={() => onWorkspaceSelect?.(workspace.id)}
              >
                {workspace.icon && (
                  <Box className={styles.iconWrapper}>
                    <SidebarIcon
                      name={workspace.icon.name}
                      size={20}
                      color={theme === 'dark' ? 'white' : 'black'}
                    />
                  </Box>
                )}
                <Typography variant="body" size="medium" color="white" theme={theme}>
                  {workspace.label}
                </Typography>
              </button>
            ))}

          {/* Settings Content */}
          {isSettings && (
            <>
              {/* Language Section */}
              {languageSelector && <Box>{languageSelector}</Box>}

              {/* Language Toggle (native vs English) */}
              {languageToggle && <Box>{languageToggle}</Box>}

              {/* Roles Section - temporarily disabled */}
              {/* {rolesDropdown && <Box>{rolesDropdown}</Box>} */}

              {/* Theme Section - temporarily disabled */}
              {/* {onToggleTheme && (
                <Box>
                  <Typography variant="label" size="small" color="gray-3" theme={theme}>
                    {labels?.appearance ?? 'Appearance'}
                  </Typography>
                  <Box pt={12}>
                    <Row gutter={[0, 0]} justify="space-between" align="middle" wrap={false}>
                      <Typography variant="body" size="medium" color="white" theme={theme}>
                        {theme === 'dark'
                          ? (labels?.darkMode ?? 'Dark Mode')
                          : (labels?.lightMode ?? 'Light Mode')}
                      </Typography>
                      <Switch
                        id="settings-theme-toggle"
                        checked={theme === 'dark'}
                        onClick={onToggleTheme}
                        theme={theme}
                      />
                    </Row>
                  </Box>
                </Box>
              )} */}
            </>
          )}
        </Box>
      </div>
    </>
  );
};

Slider.displayName = 'Slider';

export default Slider;
